import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCheckout } from '../CheckoutContext';
import offer, { CALL_PORTRAIT, euroAmount, isFilled } from '../config/offer';
import { formatCallSlot, loadCallWindow, saveCallWindow } from '../lib/callSlots';
import HOME_COPY from './openBroHomeCopy';
import COPY from './openBroV20Copy';
import './OpenBroV20.css';

/**
 * OpenBro /v20. Plan, design et argumentaire calqués sur jointherealworld.com
 * (refs/v20/site-texte.md + 20 captures). FR seulement.
 * Mockups iPhone / MacBook : vraies photos détourées (build_v3_images.py), écrans OpenBro incrustés.
 * Les CTA réservent le créneau réel (lib/callSlots) puis passent au paiement Stripe.
 */

const SUPPORT_EMAIL = 'mia@openpro.ai';
const ORIGINAL_PRICE = 99; // prix barré, comme sur /v3
const PRICE = euroAmount(offer.price);
const OLD = euroAmount(ORIGINAL_PRICE);
const DISCOUNT = Math.floor(((ORIGINAL_PRICE - offer.price) / ORIGINAL_PRICE) * 100); // -50 %, comme /v3

const STORIES = [
  { id: 'ines', src: '/ugc/12-ines-24-assiette.mp4', poster: '/ugc/12-ines-24-assiette.jpg', name: 'Inès', age: 24 },
  { id: 'lucas', src: '/ugc/14-lucas-21-vie.mp4', poster: '/ugc/14-lucas-21-vie.jpg', name: 'Lucas', age: 21 },
  { id: 'adam', src: '/ugc/15-adam-23-salle.mp4', poster: '/ugc/15-adam-23-salle.jpg', name: 'Adam', age: 23 },
  { id: 'theo', src: '/ugc/16-theo-25-business.mp4', poster: '/ugc/16-theo-25-business.jpg', name: 'Théo', age: 25 },
];

const COURSES = [
  { id: 'ia', icon: 'cpu', src: '/v10/card-ia-money.jpg' },
  { id: 'startup', icon: 'bank', src: '/v10/card-startup-ycbro.jpg' },
  { id: 'reseaux', icon: 'bag', src: '/v10/card-ecommerce-cash.jpg' },
  { id: 'montage', icon: 'cursor', src: '/formations/ig-content.mp4', poster: '/formations/ig-content.jpg', video: true },
  { id: 'musculation', icon: 'heart', src: '/v10/card-abdos.jpg' },
  { id: 'nutrition', icon: 'chart', src: '/formations/ig-food.jpg' },
];

// Grille du « plan de sortie » (réf. image 02) : 6 tuiles sur une grille 5x5.
const EXIT_TILES = [['bag', 3, 1], ['chart', 1, 2], ['cursor', 5, 2], ['people', 3, 3], ['cpu', 1, 4], ['heart', 5, 4], ['bank', 3, 5]];

// Mur « nos membres gagnent » : preuves déjà en ligne (public/v10/wins) + photos des cartes Telegram.
const WALL = [
  '/v10/wins/win-15.jpg', '/v10/result-salle.jpg', '/v10/wins/win-04.jpg', '/v10/wins/win-13.jpg', '/v10/result-chirashi-saumon.jpg',
  '/v10/wins/win-07.jpg', '/v10/wins/win-08.jpg', '/v10/result-business.jpg', '/v10/wins/win-01.jpg', '/v10/wins/win-12.jpg',
  '/v10/wins/win-14.jpg', '/v10/result-rs3.jpg', '/v10/wins/win-02.jpg', '/v10/wins/win-11.jpg', '/v10/wins/win-03.jpg',
  '/v10/wins/win-06.jpg', '/v10/wins/win-09.jpg', '/v10/wins/win-10.jpg', '/v10/wins/win-05.jpg',
];

const REVIEWS = [
  { id: 'yanis', avatar: '/avatars/young-1.jpg' },
  { id: 'sarah', avatar: '/avatars/young-2.jpg' },
  { id: 'sami', avatar: '/avatars/member-4.jpg' },
  { id: 'enzo', avatar: '/avatars/young-5.jpg' },
  { id: 'maxime', avatar: '/avatars/member-2.jpg' },
];

const PHONES = { call: '/v10/v20-iphone-call.webp', bros: '/v10/v20-iphone-bros.webp', formations: '/v10/v20-iphone-formations.webp' };

function Icon({ name }) {
  const paths = {
    arrow: 'M5 12h14M13 6l6 6-6 6',
    left: 'M19 12H5M11 6l-6 6 6 6',
    check: 'M5 12.5l4.5 4.5L19 7.5',
    cross: 'M7 7l10 10M17 7L7 17',
    plus: 'M6 9l6 6 6-6',
    clock: 'M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17zM12 7.5V12l3 2',
    lock: 'M8 11V8a4 4 0 0 1 8 0v3M6.5 11h11v9h-11z',
    menu: 'M4 7h16M4 12h10M4 17h16',
    user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4.5 20a7.5 7.5 0 0 1 15 0',
    ai: 'M4 4h10v10H4zM18 8v12H8M7 11l2-4 2 4M7.8 9.5h2.4',
    wrench: 'M14.5 6.5a4 4 0 0 0-5.3 5.3L4 17l3 3 5.2-5.2a4 4 0 0 0 5.3-5.3l-2.4 2.4-2.6-.4-.4-2.6z',
    bag: 'M6 8h12l-1 12H7zM9 8a3 3 0 0 1 6 0',
    chart: 'M4 18l5-6 4 3 7-8M15 7h5v5',
    cursor: 'M5 4l5 15 2.5-6.5L19 10zM14 14l5 5',
    people: 'M9.5 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM3 20v-1a5 5 0 0 1 10 0v1M17 8v6M14 11h6',
    cpu: 'M7 7h10v10H7zM10 10h4v4h-4zM9 3v4M15 3v4M9 17v4M15 17v4M3 9h4M3 15h4M17 9h4M17 15h4',
    heart: 'M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10zM8 12h2l1-2 2 4 1-2h2',
    bank: 'M3 9l9-5 9 5zM5 9v8M9.5 9v8M14.5 9v8M19 9v8M3 20h18',
    medal: 'M8 3h8l-2 6h-4zM12 21a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM12 12.5l1 2 2.2.3-1.6 1.5.4 2.2-2-1-2 1 .4-2.2-1.6-1.5 2.2-.3z',
    hand: 'M3 15h4l3 2h5l5-4-1.5-1.5L15 13h-4M7 15V12l4-3 4 1M14 5l2-2 3 3-2 2',
    play: 'M9 6.5v11l9-5.5z',
  };
  return (
    <svg className="v20-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d={paths[name]} />
    </svg>
  );
}

/** `**blanc gras**`, `^^orange^^`, `~~petit gris~~`, `\n` = saut de ligne. */
function Rich({ text }) {
  const tokens = text.split(/(\*\*[^*]+\*\*|\^\^[^^]+\^\^|~~[^~]+~~|\n)/);
  return tokens.map((part, index) => {
    if (part === '\n') return <br key={index} />;
    if (part.startsWith('**')) return <strong key={index}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('^^')) return <em key={index} className="v20-gold">{part.slice(2, -2)}</em>;
    if (part.startsWith('~~')) return <small key={index} className="v20-vs-word">{part.slice(2, -2)}</small>;
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function Pill({ children, icon }) {
  return <p className="v20-pill">{icon && <Icon name={icon} />}{children}</p>;
}

function OldPrice() {
  return (
    <span className="v20-price">
      <strong>{PRICE}</strong><em>{COPY.period}</em><s>{OLD}</s><b>-{DISCOUNT}&nbsp;%</b>
    </span>
  );
}

/** Vraie photo détourée (iPhone / MacBook), flottante sur une lueur. */
function Device({ src, kind, glow = 'gold', className = '' }) {
  return (
    <figure className={`v20-device v20-device-${kind} v20-glow-${glow} ${className}`}>
      <img src={src} alt="" loading="lazy" decoding="async" />
    </figure>
  );
}

function Stories() {
  const [index, setIndex] = useState(0);
  const videos = useRef({});
  const go = (step) => setIndex((current) => (current + step + STORIES.length) % STORIES.length);
  useEffect(() => {
    Object.entries(videos.current).forEach(([id, video]) => {
      if (!video) return;
      if (id === STORIES[index].id) video.play().catch(() => {});
      else video.pause();
    });
  }, [index]);
  return (
    <div className="v20-carousel">
      <button type="button" className="v20-arrow" onClick={() => go(-1)} aria-label={COPY.prev}><Icon name="left" /></button>
      <div className="v20-carousel-track">
        {STORIES.map((story, i) => {
          const offset = (i - index + STORIES.length) % STORIES.length;
          const pos = offset === 0 ? 'is-center' : offset === 1 ? 'is-right' : offset === STORIES.length - 1 ? 'is-left' : 'is-hidden';
          return (
            <figure key={story.id} className={`v20-story ${pos}`} onClick={() => setIndex(i)}>
              <video aria-label={`${story.name}, ${story.age} : ${COPY.stories[story.id]}`} ref={(node) => { videos.current[story.id] = node; }} src={story.src} poster={story.poster} muted loop playsInline preload="metadata" />
            </figure>
          );
        })}
      </div>
      <button type="button" className="v20-arrow" onClick={() => go(1)} aria-label={COPY.next}><Icon name="arrow" /></button>
    </div>
  );
}

function Toasts({ slotLabel }) {
  const [index, setIndex] = useState(0);
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % COPY.toasts.length), 7000);
    return () => window.clearInterval(timer);
  }, []);
  if (closed) return null;
  const [title, line, foot] = COPY.toasts[index](slotLabel || '—');
  return (
    <aside className={`v20-toast v20-toast-${index}`} key={index} role="status">
      <button type="button" onClick={() => setClosed(true)} aria-label="Fermer">×</button>
      <p className="v20-toast-title"><img src={CALL_PORTRAIT} alt="" />{title}</p>
      <p className="v20-toast-line">{line}</p>
      <p className="v20-toast-foot">OpenBro · <b>{foot}</b></p>
    </aside>
  );
}

export default function OpenBroV20() {
  const { startCheckout, isActive } = useCheckout();
  const [callWindow, setCallWindow] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [filmOn, setFilmOn] = useState(false);
  const filmRef = useRef(null);

  const slots = (callWindow?.slots || []).map((iso) => formatCallSlot(iso, 'fr'));
  const selected = slots.find((slot) => slot.iso === callWindow?.selected) || slots[0] || null;

  useEffect(() => {
    setCallWindow(loadCallWindow());
    document.documentElement.lang = 'fr';
    document.title = 'OpenBro · Le mentorat privé de Gaut Bonac';
  }, []);

  const join = (source) => {
    if (isActive) return;
    if (isFilled(offer.paymentUrl)) {
      window.location.assign(offer.paymentUrl);
      return;
    }
    if (!selected) {
      startCheckout(source);
      return;
    }
    startCheckout(source, {
      callAt: selected.iso,
      callLabel: selected.label,
      callTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  };

  const chooseSlot = (iso) => {
    setCallWindow((current) => {
      if (!current) return current;
      const next = { ...current, selected: iso };
      saveCallWindow(next);
      return next;
    });
  };

  /** Bouton orange + ligne de preuve, répétés après chaque bloc (réf. images 01, 07, 12, 15, 20). */
  const Cta = ({ source, label = COPY.cta, proof = true, block = false }) => (
    <div className={block ? 'v20-cta v20-cta-block' : 'v20-cta'}>
      <button type="button" className="v20-btn" disabled={isActive} onClick={() => join(source)}>
        {isActive ? '…' : label}
      </button>
      {proof && (
        <p className="v20-proof">
          <span className="v20-faces">
            <img src={CALL_PORTRAIT} alt="" />
            <img src="/v10/campus-musculation.jpg" alt="" />
            <img src="/v10/campus-nutrition.jpg" alt="" />
            <img src="/v10/campus-ia.jpg" alt="" />
          </span>
          <span><Rich text={COPY.proof} /></span>
        </p>
      )}
      {proof && selected && <p className="v20-slot-line"><Icon name="clock" /><Rich text={COPY.slotLine(selected.label)} /></p>}
    </div>
  );

  return (
    <div className="v20">
      <header className="v20-header">
        <div className="v20-wrap v20-header-row">
          <button type="button" className="v20-menu" onClick={() => setMenuOpen((v) => !v)} aria-expanded={menuOpen}>
            <Icon name="menu" />{COPY.menu}
          </button>
          <a className="v20-logo" href="#top" aria-label="OpenBro"><img src={CALL_PORTRAIT} alt="" /></a>
          <Link className="v20-login" to="/login">{COPY.login}</Link>
        </div>
        {menuOpen && (
          <nav className="v20-drawer" aria-label="OpenBro">
            {COPY.nav.map(([href, label]) => <a key={href} href={href} onClick={() => setMenuOpen(false)}>{label}</a>)}
          </nav>
        )}
      </header>

      {/* Hero (site) */}
      <section className="v20-hero" id="top">
        <div className="v20-hero-bg" aria-hidden="true" style={{ backgroundImage: 'url(/v3-board.jpg)' }} />
        <div className="v20-wrap v20-hero-inner">
          <h1 className="v20-duo-h"><Rich text={COPY.heroTitle} /></h1>
          <p className="v20-hero-sub">{COPY.heroSub}</p>
          <p className="v20-lead v20-duo"><Rich text={COPY.heroLead} /></p>
          <Cta source="v20-hero" />
          <ul className="v20-pills">
            {COPY.pills.map(([a, b]) => <li key={a}><strong>{a}</strong><span>{b}</span></li>)}
          </ul>
        </div>
      </section>

      {/* Avis (site) : cartes qui défilent */}
      <section className="v20-section v20-reviews">
        <div className="v20-wrap v20-center">
          <h2 className="v20-h2 v20-duo-h"><Rich text={COPY.reviewsTitle} /></h2>
          <p className="v20-note">{COPY.reviewsNote}</p>
        </div>
        <div className="v20-marquee">
          <div className="v20-marquee-track">
            {[...REVIEWS, ...REVIEWS].map((r, i) => (
              <article key={`${r.id}-${i}`} className="v20-review">
                <header><img src={r.avatar} alt="" /><strong>{r.id[0].toUpperCase() + r.id.slice(1)}</strong><span>OpenBro · Les bros</span></header>
                <p>{HOME_COPY.fr.wins[r.id]}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Offres (site) */}
      <section className="v20-section" id="offre">
        <div className="v20-wrap v20-center">
          <Pill>{COPY.plansKicker}</Pill>
          <h2 className="v20-h2 v20-duo-h"><Rich text={COPY.plansTitle} /></h2>
          <article className="v20-plan">
            <p className="v20-plan-label">{COPY.plansLabel}</p>
            <h3>{COPY.plan.name}</h3>
            <OldPrice />
            <ul>{COPY.plan.items.map((item) => <li key={item}><Icon name="check" />{item}</li>)}</ul>
            {slots.length > 0 && (
              <div className="v20-slots" role="radiogroup" aria-label="Créneaux">
                {slots.map((slot) => (
                  <button key={slot.iso} type="button" role="radio" aria-checked={selected?.iso === slot.iso} className={selected?.iso === slot.iso ? 'is-on' : undefined} onClick={() => chooseSlot(slot.iso)}>
                    {slot.chip}
                  </button>
                ))}
              </div>
            )}
            <Cta source="v20-plan" label={selected ? COPY.bookCta(selected.label) : COPY.cta} proof={false} block />
            <p className="v20-fine"><Icon name="lock" />{COPY.secure} · {COPY.cancel}</p>
          </article>
        </div>
      </section>

      {/* 1. Trois étapes (image 01) */}
      <section className="v20-section" id="etapes">
        <div className="v20-wrap v20-center">
          <Pill>{COPY.stepsPill}</Pill>
          <h2 className="v20-h2 v20-duo-h"><Rich text={COPY.stepsTitle} /></h2>
          <ol className="v20-steps">
            {COPY.steps.map(([kicker, text], i) => (
              <li key={kicker}>
                <div className="v20-step-shot"><img src={['/product/call-mobile.jpg', '/v20/step-bros.jpg', '/product/nutrition.jpg'][i]} alt="" loading="lazy" /></div>
                <span className="v20-step-k">{kicker}</span>
                <strong>{text}</strong>
              </li>
            ))}
          </ol>
          <Cta source="v20-steps" />
        </div>
      </section>

      {/* 2. Le plan de sortie (image 02) */}
      <section className="v20-section">
        <div className="v20-wrap v20-split">
          <div>
            <h2 className="v20-h2 v20-left">{COPY.exitTitle.replace(/\*\*/g, '')}</h2>
            <div className="v20-duo v20-body">{COPY.exit.map((t) => <p key={t}><Rich text={t} /></p>)}</div>
          </div>
          <div className="v20-exit">
            <p className="v20-exit-title">{COPY.exitGridTitle}</p>
            <div className="v20-exit-grid">
              {EXIT_TILES.map(([icon, col, row]) => <span key={`${col}-${row}`} style={{ gridColumn: col, gridRow: row }}><Icon name={icon} /></span>)}
            </div>
          </div>
        </div>
      </section>

      {/* 3. Leur façon contre la nôtre (image 03) */}
      <section className="v20-section" id="voies">
        <div className="v20-wrap v20-center">
          <Pill>{COPY.waysPill}</Pill>
          <h2 className="v20-h2"><Rich text={COPY.waysTitle} /></h2>
          <div className="v20-ways">
            <article className="v20-way v20-way-off">
              <h3>{COPY.theirTitle}</h3>
              <ul>{COPY.theirs.map((t) => <li key={t}><Icon name="check" />{t}</li>)}</ul>
              <a className="v20-btn v20-btn-dim" href={HOME_COPY.fr.aloneHref} target="_blank" rel="noopener noreferrer">{COPY.ctaTheirs}</a>
            </article>
            <span className="v20-versus">{COPY.versus}</span>
            <article className="v20-way v20-way-on">
              <h3><img src={CALL_PORTRAIT} alt="" />OpenBro</h3>
              <ul>{COPY.ours.map((t) => <li key={t}><Icon name="check" />{t}</li>)}</ul>
              <Cta source="v20-ways" proof={false} block />
            </article>
          </div>
        </div>
      </section>

      {/* 4. Témoignages (image 04) */}
      <section className="v20-section">
        <div className="v20-wrap v20-center">
          <Pill>{COPY.storiesPill}</Pill>
          <h2 className="v20-h2 v20-duo-h"><Rich text={COPY.storiesTitle} /></h2>
          <p className="v20-q">{COPY.storiesQ}</p>
          <div className="v20-duo v20-body v20-body-center">{COPY.storiesBody.map((t) => <p key={t}><Rich text={t} /></p>)}</div>
        </div>
        <Stories />
        <div className="v20-wrap v20-center"><Cta source="v20-stories" /></div>
      </section>

      {/* Diplôme (site) */}
      <section className="v20-section">
        <div className="v20-wrap v20-split">
          <div className="v20-seal" aria-hidden="true"><Icon name="medal" /><span>OpenBro</span><em>by Gaut Bonac</em></div>
          <div>
            <Pill icon="medal">{COPY.diplomaPill}</Pill>
            <h2 className="v20-h2 v20-left v20-duo-h"><Rich text={COPY.diplomaTitle} /></h2>
            <p className="v20-duo v20-body-p"><Rich text={COPY.diploma} /></p>
            <ul className="v20-checks">{COPY.diplomaList.map((t) => <li key={t}><Icon name="check" />{t}</li>)}</ul>
          </div>
        </div>
      </section>

      {/* 5. Le monde change (images 05, 06, 07) */}
      <section className="v20-section">
        <div className="v20-wrap v20-center">
          <p className="v20-news"><b>{COPY.worldPill[0]}</b>{COPY.worldPill[1]}</p>
          <h2 className="v20-h2">{COPY.worldTitle}</h2>
        </div>
        <div className="v20-wrap v20-world">
          {[['time', 'clock', '/v10/v20-mac-nutrition.webp'], ['ai', 'ai', '/v10/v20-mac-campus.webp'], ['skill', 'wrench', '/v10/v20-mac-formations.webp']].map(([key, icon, mac], i) => (
            <article key={key} className={i % 2 ? 'v20-world-row is-flip' : 'v20-world-row'}>
              <div className="v20-world-text">
                <Pill icon={icon}>{COPY[key].pill}</Pill>
                <h3 className="v20-h3 v20-duo-h"><Rich text={COPY[key].title} /></h3>
                <div className="v20-duo v20-body">{COPY[key].body.map((t) => <p key={t}><Rich text={t} /></p>)}</div>
              </div>
              <div className="v20-world-media"><Device src={mac} kind="mac" glow="dark" /></div>
            </article>
          ))}
        </div>
        <div className="v20-wrap v20-center"><Cta source="v20-world" /></div>
      </section>

      {/* 6. Réserve pour l'année (image 08) */}
      <section className="v20-section v20-year">
        <div className="v20-wrap v20-split">
          <div>
            <Pill>{COPY.yearPill}</Pill>
            <h2 className="v20-h2 v20-left v20-duo-h"><Rich text={COPY.yearTitle} /></h2>
            <div className="v20-duo v20-body">{COPY.year.map((t) => <p key={t}><Rich text={t} /></p>)}</div>
            <Cta source="v20-year" proof={false} />
          </div>
          <div className="v20-rocks"><Device src={PHONES.formations} kind="phone" glow="gold" className="v20-tilt-r" /></div>
        </div>
      </section>

      {/* 7. Deux voies (image 09) */}
      <section className="v20-section" id="deux-voies">
        <div className="v20-wrap v20-center">
          <p className="v20-news"><b>Nouveautés :</b>Présentation d’OpenBro</p>
          <h2 className="v20-h2">{COPY.pathsTitle}</h2>
          <div className="v20-film">
            <video ref={filmRef} src="/openbro-film-fr.mp4" poster="/openbro-film-fr.jpg" controls={filmOn} playsInline preload="none" />
            {!filmOn && (
              <button type="button" className="v20-play" aria-label="Lancer la vidéo" onClick={() => { setFilmOn(true); filmRef.current?.play().catch(() => {}); }}>
                <Icon name="play" />
              </button>
            )}
          </div>
          <div className="v20-tree" aria-hidden="true" />
          <div className="v20-forks">
            <div className="v20-fork">
              <p className="v20-fork-k">{COPY.stayKicker}</p>
              <h3>{COPY.stayTitle}</h3>
              <p className="v20-fork-p">{COPY.stay}</p>
              <a className="v20-btn v20-btn-dim" href={HOME_COPY.fr.aloneHref} target="_blank" rel="noopener noreferrer">{COPY.ctaTheirs}</a>
            </div>
            <span className="v20-vs-dot">vs</span>
            <div className="v20-fork v20-fork-on">
              <p className="v20-fork-k">{COPY.payKicker}</p>
              <h3 className="v20-gold-price"><OldPrice /></h3>
              <p className="v20-fork-p">{COPY.pay}</p>
              <Cta source="v20-paths" proof={false} />
            </div>
          </div>
        </div>
      </section>

      {/* 8. Formations (images 10, 11) */}
      <section className="v20-section" id="formations">
        <div className="v20-wrap v20-center">
          <Pill>{COPY.coursesPill}</Pill>
          <h2 className="v20-h2">{COPY.coursesTitle}</h2>
          <ul className="v20-courses">
            {COURSES.map((c) => {
              const [title, text] = COPY.courses[c.id];
              return (
                <li key={c.id}>
                  <div className="v20-course-head">
                    <Icon name={c.icon} />
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </div>
                  <div className="v20-course-media">
                    {c.video ? <video src={c.src} poster={c.poster} muted autoPlay loop playsInline preload="metadata" /> : <img src={c.src} alt="" loading="lazy" decoding="async" />}
                    <span className="v20-course-play" aria-hidden="true"><Icon name="play" /></span>
                  </div>
                </li>
              );
            })}
          </ul>
          {/* 9. Nouvelles compétences (image 12) */}
          <div className="v20-newskills">{COPY.newSkills.map((t) => <p key={t}><Rich text={t} /></p>)}</div>
          <Cta source="v20-courses" />
        </div>
      </section>

      {/* 10. Nos membres gagnent (site + image 12) */}
      <section className="v20-section" id="bros">
        <div className="v20-wrap v20-center">
          <Pill>{COPY.winsPill}</Pill>
          <h2 className="v20-h2 v20-duo-h v20-swirl"><Rich text={COPY.winsTitle} /></h2>
          <ul className="v20-wall">{WALL.map((src) => <li key={src}><img src={src} alt="" loading="lazy" decoding="async" /></li>)}</ul>
          <Cta source="v20-wall" />
        </div>
      </section>

      {/* 11. Prêt à travailler dur (image 13) */}
      <section className="v20-section">
        <div className="v20-wrap v20-split">
          <div>
            <Pill>{COPY.workPill}</Pill>
            <h2 className="v20-h2 v20-left v20-duo-h"><Rich text={COPY.workTitle} /></h2>
            <div className="v20-duo v20-body">{COPY.work.map((t) => <p key={t}><Rich text={t} /></p>)}</div>
            <Cta source="v20-work" proof={false} />
          </div>
          <div className="v20-two-phones">
            <Device src={PHONES.bros} kind="phone" glow="dark" className="v20-tilt-l" />
            <Device src={PHONES.call} kind="phone" glow="dark" className="v20-tilt-r" />
          </div>
        </div>
      </section>

      {/* 12. Tu auras accès à (images 14, 15) */}
      <section className="v20-section">
        <div className="v20-wrap v20-center">
          <h2 className="v20-h2">{COPY.accessTitle}</h2>
          <div className="v20-access">
            {COPY.access.map((row) => (
              <article key={row.title}>
                <div className="v20-access-phone"><img src={PHONES[row.phone]} alt="" loading="lazy" /></div>
                <div className="v20-access-body">
                  <h3>{row.title}</h3>
                  <ul>{row.items.map((t) => <li key={t}><Icon name="check" /><span><Rich text={t} /></span></li>)}</ul>
                </div>
              </article>
            ))}
          </div>
          <Cta source="v20-access" />
        </div>
      </section>

      {/* 13. Le choix (image 16) */}
      <section className="v20-section" id="choix">
        <div className="v20-wrap v20-center">
          <h2 className="v20-h2">{COPY.choiceTitle}</h2>
          <div className="v20-choice">
            <article className="v20-choice-off">
              <Icon name="clock" />
              <h3>{COPY.nothingTitle}</h3>
              <p className="v20-choice-sub">{COPY.nothingSub}</p>
              <p className="v20-choice-cost">{COPY.nothingCost}</p>
              <ul>{COPY.nothing.map((t) => <li key={t}><Icon name="check" />{t}</li>)}</ul>
              <a className="v20-btn v20-btn-dim" href={COPY.nothingHref} rel="noopener noreferrer">{COPY.ctaYours}</a>
            </article>
            <article className="v20-choice-on">
              <Icon name="hand" />
              <h3>{COPY.actTitle}</h3>
              <p className="v20-choice-sub">{COPY.actSub}</p>
              <div className="v20-choice-price"><OldPrice /></div>
              <ul>{COPY.act.map((t) => <li key={t}><Icon name="check" />{t}</li>)}</ul>
              <div className="v20-choice-foot">
                <p><Icon name="cross" />{COPY.cancel}</p>
                <p className="v20-lock-line"><Icon name="lock" />{COPY.lockPrice(PRICE)}</p>
                <p className="v20-lock-note">{COPY.lockNote}</p>
              </div>
              <Cta source="v20-choice" proof={false} block />
            </article>
          </div>
        </div>
      </section>

      {/* 14. Bloque ton prix (image 17) */}
      <section className="v20-section v20-lock">
        <div className="v20-wrap v20-center">
          <Pill>{COPY.lockPill}</Pill>
          <div className="v20-duo v20-lock-text">{COPY.lock.map((t) => <p key={t}><Rich text={t} /></p>)}</div>
        </div>
      </section>

      {/* 15. FAQ (images 17-20) */}
      <section className="v20-section" id="faq">
        <div className="v20-wrap v20-center">
          <Pill>{COPY.faqPill}</Pill>
          <h2 className="v20-h2">{COPY.faqTitle}</h2>
          <div className="v20-faq">
            {COPY.faq.map(([q, a], i) => {
              const open = openFaq === i;
              return (
                <div key={q} className={open ? 'v20-qa is-open' : 'v20-qa'}>
                  <button type="button" aria-expanded={open} onClick={() => setOpenFaq(open ? null : i)}>{q}<Icon name="plus" /></button>
                  {open && <p><Rich text={a} /></p>}
                </div>
              );
            })}
          </div>
          <Cta source="v20-faq" />
        </div>
      </section>

      <footer className="v20-footer">
        <div className="v20-wrap v20-footer-row">
          <div>
            <p className="v20-footer-brand">{COPY.footerBrand}</p>
            <p>{COPY.footerTag}</p>
            <nav aria-label="Legal">
              <Link to="/terms">{COPY.terms}</Link>
              <Link to="/privacy">{COPY.privacy}</Link>
            </nav>
          </div>
          <div className="v20-footer-help">
            <p>{COPY.help}</p>
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            <Link className="v20-login" to="/login">{COPY.login}</Link>
          </div>
          <p className="v20-disclaimer">{COPY.disclaimer}</p>
          <p className="v20-copy">© {new Date().getFullYear()} OpenBro.</p>
        </div>
      </footer>

      <Toasts slotLabel={selected?.label} />
      <style>{'html { scroll-behavior: smooth; }'}</style>
    </div>
  );
}
